import React, { useState, useEffect, useRef, useCallback } from "react";

interface Testimonial {
  quote: string[];
  recommender: string;
}

interface RecommendationHeroProps {
  testimonials: Testimonial[];
  interval?: number;
}

const RecommendationHero: React.FC<RecommendationHeroProps> = ({
  testimonials,
  interval = 15000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef<number | null>(null);

  const stopAutoPlay = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    if (testimonials.length <= 1 || isHovered) return;
    stopAutoPlay();
    timerRef.current = window.setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1,
      );
    }, interval);
  }, [testimonials, interval, isHovered, stopAutoPlay]);

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [startAutoPlay, stopAutoPlay]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const goToSlide = (index: number) => {
    stopAutoPlay();
    setCurrentIndex(index);
    if (!isHovered) setTimeout(startAutoPlay, interval);
  };

  const nextSlide = () => {
    stopAutoPlay();
    setCurrentIndex((prevIndex) =>
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1,
    );
    if (!isHovered) setTimeout(startAutoPlay, interval);
  };

  const prevSlide = () => {
    stopAutoPlay();
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1,
    );
    if (!isHovered) setTimeout(startAutoPlay, interval);
  };

  if (testimonials.length === 0) return null;

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section
      className="relative flex min-h-[400px] items-center justify-center overflow-hidden dark:bg-neutral-950 px-4 py-16 sm:px-6 lg:px-8"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        aria-hidden="true"
        className="absolute left-4 top-4 text-9xl font-extrabold text-gray-400 dark:text-amber-500 opacity-75 sm:left-8 sm:top-8 lg:left-80 lg:top-16"
      >
        &#8220;
      </span>
      <span
        aria-hidden="true"
        className="absolute bottom-4 right-4 text-9xl font-extrabold text-gray-400 dark:text-amber-500 opacity-75 sm:bottom-8 sm:right-8 lg:bottom-16 lg:right-80"
      >
        &#8221;
      </span>

      {testimonials.length > 1 && (
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 dark:bg-neutral-800/70 text-gray-800 dark:text-white rounded-full hover:bg-white dark:hover:bg-neutral-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 sm:left-8 lg:left-24"
          aria-label="Previous testimonial"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      )}

      <div className="relative z-10 mx-auto max-w-3xl text-center">
        {currentTestimonial.quote.map((paragraph, pIndex) => (
          <p
            key={`${currentIndex}-${pIndex}`}
            className={`text-sm font-semibold leading-relaxed text-gray-800 dark:text-white sm:text-sm lg:text-base ${
              pIndex > 0 ? "mt-4" : ""
            } animate-fade-in`}
          >
            {pIndex === 0 && '"'}
            {paragraph}
            {pIndex === currentTestimonial.quote.length - 1 && '"'}
          </p>
        ))}
        <p className="mt-8 text-xs font-medium text-gray-600 dark:text-white sm:text-xs animate-fade-in-delayed">
          — {currentTestimonial.recommender}
        </p>
      </div>

      {testimonials.length > 1 && (
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full dark:bg-neutral-800/70 text-gray-800 dark:text-white hover:bg-white dark:hover:bg-neutral-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 sm:right-8 lg:right-24"
          aria-label="Next testimonial"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      )}

      {testimonials.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-3 w-3 rounded-full transition-colors duration-300 ${
                index === currentIndex
                  ? "bg-amber-500 dark:bg-amber-500"
                  : "bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600"
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default RecommendationHero;
